import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { Box, FormLabel, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { modalZIndex } from 'theme';
// Extend StyledDay to accept a 'clickable' prop
const StyledDay = styled('div')(({ availabilityStatus, clickable }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: availabilityStatus === 'unavailable'
        ? '#b0b0b0'
        : availabilityStatus === 'hourlyOnly'
            ? '#007bff'
            : availabilityStatus === 'dailyAndHourly'
                ? '#28a745'
                : availabilityStatus === 'waitlisted'
                    ? 'purple'
                    : 'transparent',
    color: availabilityStatus === 'unavailable' || availabilityStatus === 'waitlisted'
        ? '#ffffff'
        : '#000000',
    cursor: clickable ? 'pointer' : 'default',
    transition: 'background-color 0.3s, color 0.3s',
    '&:hover': clickable
        ? {
            opacity: 0.8,
        }
        : {},
}));
const CustomDatePicker = ({ label, value, onDayClick, onChange, shouldDisableDate, getAvailabilityStatus, minDate, }) => {
    /**
     * Determines the style and interactivity of each day.
     * @param day - The day being rendered.
     * @param isDisabled - Whether the day is "selected" or "disabled" in MUI logic (not used much here).
     * @returns An object containing availability status, tooltip text, and clickability.
     */
    const getDayStyle = (day, isDisabled) => {
        if (getAvailabilityStatus) {
            const availabilityStatus = getAvailabilityStatus(day);
            // Decide what the tooltip says based on status
            let tooltipTitle = 'Available for daily and hourly bookings';
            if (availabilityStatus === 'waitlisted') {
                tooltipTitle = 'This entire day is on the waitlist (fully booked).';
            }
            else if (availabilityStatus === 'unavailable') {
                tooltipTitle = 'Unavailable';
            }
            else if (availabilityStatus === 'hourlyOnly') {
                tooltipTitle = 'Hourly bookings only for this day';
            }
            return {
                availabilityStatus,
                tooltipTitle,
                // By default below, only waitlisted days are clickable.
                // Adjust if you'd like "available" days to be clickable as well.
                clickable: availabilityStatus === 'waitlisted',
            };
        }
        // If no getAvailabilityStatus function is passed, fall back to a default
        return {
            availabilityStatus: isDisabled ? 'unavailable' : 'dailyAndHourly',
            tooltipTitle: isDisabled ? 'Unavailable' : 'Available',
            clickable: false,
        };
    };
    return (_jsxs(Box, { children: [_jsx(FormLabel, { sx: {
                    fontWeight: 'bold',
                    color: 'var(--secondaryFgColor)',
                    marginBottom: '0.5rem',
                    marginRight: '0.5rem',
                    display: 'flex',
                    justifyContent: 'center',
                }, children: label }), _jsx(DesktopDatePicker, { value: value, onChange: onChange, shouldDisableDate: shouldDisableDate, minDate: minDate, slotProps: {
                    popper: {
                        sx: {
                            zIndex: modalZIndex,
                        },
                    },
                    day: ({ day, selected }) => {
                        const { availabilityStatus, tooltipTitle, clickable } = getDayStyle(day, selected);
                        return {
                            children: (_jsx(Tooltip, { title: tooltipTitle, children: _jsx(StyledDay, { availabilityStatus: availabilityStatus, clickable: clickable, onClick: () => {
                                        if (clickable && onDayClick) {
                                            onDayClick(day);
                                        }
                                    }, children: day.date() }) })),
                            disabled: availabilityStatus === 'unavailable', // Only disable 'unavailable' days
                        };
                    },
                } })] }));
};
export default CustomDatePicker;
